import { useState, type ReactNode, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1, Calendar, User, Edit2, type Icon } from "iconsax-react";
import { TengeCircleIcon } from "@/shared/assets/icons";
import { ModalForm, Button, Prompt, Skeleton, FileViewer, PromptForm, Badge, Toast } from "@/shared/ui";
import { PERMISSIONS } from "@/shared/consts";
import { formatDateForDisplay, downloadBlob, ensurePdfExtension, formatMoneyKzt, type Locale } from "@/shared/utils";
import { ReviewNoteModal, DetailFieldRow, DetailModalFooterActions } from "@/features/hr/shared";
import { useAuth } from "@/shared/hooks";
import { RevokeSigningButton, useOrderSigning } from "@/features/signing";
import {
  useGetAmendmentDetailQuery,
  useSubmitAgreementMutation,
  usePreviewApplicationMutation,
  usePreviewAmendmentOrderMutation,
  useSignAmendmentOrderMutation,
  useApproveAmendmentMutation,
  useRejectAmendmentMutation,
  useRequestRevisionMutation,
  useRevokeAmendmentMutation,
  usePreviewAgreementMutation,
} from "../api";
import type { AmendmentStatus, PositionValuesJson } from "../types";
import { getAvailableActions, filterActionsByPermissions, type AmendmentAction } from "../utils/amendmentRules";
import { AMENDMENT_STATUS_MAP } from "../consts";

interface Props {
  amendmentId: number;
  onClose: () => void;
  onSignSuccess?: () => void;
}

interface AmendmentField {
  key: string;
  label: string;
  value: string;
  icon: Icon | ComponentType<{ size?: number; color?: string }>;
  isStatus?: boolean;
  statusComponent?: ReactNode;
}

export default function AmendmentDetailsModal({ amendmentId, onClose, onSignSuccess }: Props) {
  const { t, i18n } = useTranslation("ContractsPage");
  const locale = (i18n.language as Locale) || "ru";
  const { hasPermission, hasRole, user } = useAuth();

  const { data: amendmentData, isLoading, isError, refetch: refetchAmendment } = useGetAmendmentDetailQuery(amendmentId);
  const [submitAgreement] = useSubmitAgreementMutation();
  const [previewAgreement] = usePreviewAgreementMutation();
  const [previewApplication] = usePreviewApplicationMutation();
  const [previewOrder, { isLoading: isDownloadingOrder }] = usePreviewAmendmentOrderMutation();
  const [approveAmendment] = useApproveAmendmentMutation();
  const [rejectAmendment, { isLoading: isRejecting }] = useRejectAmendmentMutation();
  const [signOrder] = useSignAmendmentOrderMutation();
  const [requestRevision, { isLoading: isRequestingRevision }] = useRequestRevisionMutation();
  const [revokeAmendment, { isLoading: isRevoking }] = useRevokeAmendmentMutation();

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) {
      onSignSuccess?.();
    }
  }

  const { t: tCommon } = useTranslation("Common");

  const applicationSigning = useOrderSigning({
    previewFn() {
      return previewApplication(amendmentId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature) {
      return approveAmendment({ id: amendmentId, cms_signature: cmsSignature }).unwrap();
    },
    skipFileUpload: true,
    onSuccess() {
      setPrompt({
        title: t("amendment.actions.approveSigningTitle"),
        text: t("amendment.actions.approveSigningText"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  const orderSigning = useOrderSigning({
    previewFn() {
      return previewOrder(amendmentId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature, fileBase64) {
      if (!amendmentData?.order_id) throw new Error("No order ID");
      return signOrder({
        orderId: amendmentData.order_id,
        amendmentId,
        cms_signature: cmsSignature,
        file_base64: fileBase64!,
      }).unwrap();
    },
    onSuccess() {
      setPrompt({
        title: tCommon("signing.success.title"),
        text: tCommon("signing.success.text"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  const agreementSigning = useOrderSigning({
    previewFn() {
      return previewAgreement(amendmentId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature, fileBase64) {
      return submitAgreement({ id: amendmentId, cms_signature: cmsSignature, file_base64: fileBase64! }).unwrap();
    },
    onSuccess() {
      setPrompt({
        title: t("amendment.actions.agreementSubmittedTitle"),
        text: t("amendment.actions.agreementSubmittedText"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  const isSubmittingAgreement = agreementSigning.isLoading;
  const isApproving = applicationSigning.isLoading;

  const [showRejectNoteModal, setShowRejectNoteModal] = useState(false);
  const [showRevisionNoteModal, setShowRevisionNoteModal] = useState(false);
  const [showConfirmSubmitAgreement, setShowConfirmSubmitAgreement] = useState(false);
  const [showConfirmRevoke, setShowConfirmRevoke] = useState(false);
  const [previewingOrderAmendmentId, setPreviewingOrderAmendmentId] = useState<number | null>(null);

  function handleConfirmSubmitAgreement() {
    setShowConfirmSubmitAgreement(false);
    agreementSigning.handleSign();
  }

  function handleRejectDraft() {
    setShowRejectNoteModal(true);
  }

  async function handleRejectWithNote(note: string) {
    if (isRejecting) return;

    try {
      await rejectAmendment({
        id: amendmentId,
        data: { note: note.trim() || undefined },
      }).unwrap();
      setShowRejectNoteModal(false);
      setPrompt({
        title: t("amendment.actions.rejectSuccessTitle"),
        text: t("amendment.actions.rejectSuccessText"),
        variant: "success",
      });
    } catch {
      setPrompt({
        title: t("amendment.actions.errorTitle"),
        text: t("amendment.actions.rejectError"),
        variant: "error",
      });
    }
  }

  async function handleRequestRevisionWithNote(note: string) {
    if (isRequestingRevision) return;

    try {
      await requestRevision({
        id: amendmentId,
        data: { note: note.trim() },
      }).unwrap();
      setShowRevisionNoteModal(false);
      setPrompt({
        title: t("amendment.actions.requestRevisionSuccessTitle"),
        text: t("amendment.actions.requestRevisionSuccessText"),
        variant: "success",
      });
    } catch {
      setPrompt({
        title: t("amendment.actions.errorTitle"),
        text: t("amendment.actions.requestRevisionError"),
        variant: "error",
      });
    }
  }

  async function handleRevokeConfirm() {
    if (isRevoking) return;

    try {
      await revokeAmendment(amendmentId).unwrap();
      setShowConfirmRevoke(false);
      setPrompt({
        title: t("amendment.actions.revokeSuccessTitle"),
        text: t("amendment.actions.revokeSuccessText"),
        variant: "success",
      });
    } catch {
      setShowConfirmRevoke(false);
      setPrompt({
        title: t("amendment.actions.errorTitle"),
        text: t("amendment.actions.revokeError"),
        variant: "error",
      });
    }
  }

  async function handleDownloadOrderDocx() {
    if (previewingOrderAmendmentId !== null) return;

    try {
      setPreviewingOrderAmendmentId(amendmentId);
      const { blob, filename } = await previewOrder(amendmentId).unwrap();
      downloadBlob(blob, ensurePdfExtension(filename || t("downloads.amendmentOrderFileName", { amendmentId })));
    } catch {
      setPrompt({
        title: t("amendment.actions.errorTitle"),
        text: t("amendment.actions.downloadOrderDocxError"),
        variant: "error",
      });
    } finally {
      setPreviewingOrderAmendmentId(null);
    }
  }

  function getStatusBadge(amendment: NonNullable<typeof amendmentData>, amendmentStatus: AmendmentStatus) {
    if (amendmentStatus === "draft" && amendment.application_review_note) {
      return (
        <Badge
          variant="soft"
          color="notice"
          text={t("amendment.statusLabels.revision_requested")}
          icon={<Edit2 size={14} color="currentColor" />}
        />
      );
    }
    const config = AMENDMENT_STATUS_MAP[amendmentStatus];
    return <Badge variant="soft" color={config.color} text={t(config.label)} icon={config.icon} />;
  }

  function handleAction(action: AmendmentAction) {
    switch (action) {
      case "approve":
        applicationSigning.handleSign();
        break;
      case "reject_draft":
        handleRejectDraft();
        break;
      case "request_revision":
        setShowRevisionNoteModal(true);
        break;
      case "revoke":
        setShowConfirmRevoke(true);
        break;
      case "sign_order":
        orderSigning.handleSign();
        break;
      case "download_order_docx":
        handleDownloadOrderDocx();
        break;
      case "submit_agreement":
        setShowConfirmSubmitAgreement(true);
        break;
    }
  }

  function filterField(field: AmendmentField): boolean {
    return !!(field.value || field.isStatus);
  }

  function renderField(field: AmendmentField, index: number, array: AmendmentField[]) {
    return (
      <DetailFieldRow
        key={field.key}
        label={field.label}
        value={field.value}
        icon={field.icon}
        isStatus={field.isStatus}
        statusComponent={field.statusComponent}
        isLast={index === array.length - 1}
      />
    );
  }

  function isPositionValuesJson(value: unknown): value is PositionValuesJson {
    return (
      typeof value === "object" &&
      value !== null &&
      ("job_position_ru" in value || "job_position_kk" in value || "job_duties_ru" in value || "job_duties_kk" in value)
    );
  }

  function renderAmendmentContent(amendment: NonNullable<typeof amendmentData>) {
    const status = amendment.status as AmendmentStatus;

    const shouldShowApplicationPdf =
      amendment.application_document.signed_url || amendment.application_document.original_url;

    const isPositionAmendment =
      (amendment.new_values_json && isPositionValuesJson(amendment.new_values_json)) ||
      (amendment.old_values_json && isPositionValuesJson(amendment.old_values_json));

    const isSalaryAmendment =
      (amendment.new_values_json && "salary_amount" in amendment.new_values_json) ||
      (amendment.old_values_json && "salary_amount" in amendment.old_values_json);

    const fields: AmendmentField[] = [
      {
        key: "effectiveDate",
        label: t("amendment.effectiveDate"),
        value: amendment.effective_date ? formatDateForDisplay(amendment.effective_date, false) : "",
        icon: Calendar,
      },
      {
        key: "worker",
        label: t("amendment.worker"),
        value: amendment.worker.full_name || "",
        icon: User,
      },
      {
        key: "clause",
        label: t("amendment.clause"),
        value: amendment.clause.section_number || "",
        icon: DocumentText1,
      },
    ];

    if (isPositionAmendment) {
      const oldPositionValues =
        amendment.old_values_json && isPositionValuesJson(amendment.old_values_json) ? amendment.old_values_json : null;
      const newPositionValues =
        amendment.new_values_json && isPositionValuesJson(amendment.new_values_json) ? amendment.new_values_json : null;

      const oldPosition =
        locale === "ru"
          ? oldPositionValues?.job_position_ru
          : oldPositionValues?.job_position_kk || oldPositionValues?.job_position_ru;
      const newPosition =
        locale === "ru"
          ? newPositionValues?.job_position_ru
          : newPositionValues?.job_position_kk || newPositionValues?.job_position_ru;

      if (oldPosition) {
        fields.push({
          key: "oldPosition",
          label: t("amendment.oldPosition"),
          value: String(oldPosition),
          icon: DocumentText1,
        });
      }

      if (newPosition) {
        fields.push({
          key: "newPosition",
          label: t("amendment.newPosition"),
          value: String(newPosition),
          icon: DocumentText1,
        });
      }
    }

    if (isSalaryAmendment) {
      const oldSalary =
        amendment.old_values_json && "salary_amount" in amendment.old_values_json
          ? amendment.old_values_json.salary_amount
          : undefined;
      const newSalary =
        amendment.new_values_json && "salary_amount" in amendment.new_values_json
          ? amendment.new_values_json.salary_amount
          : undefined;

      if (oldSalary) {
        fields.push({
          key: "oldSalary",
          label: t("amendment.oldSalary"),
          value: formatMoneyKzt(oldSalary, locale),
          icon: TengeCircleIcon,
        });
      }

      if (newSalary) {
        fields.push({
          key: "newSalary",
          label: t("amendment.newSalary"),
          value: formatMoneyKzt(newSalary, locale),
          icon: TengeCircleIcon,
        });
      }
    }

    if (amendment.application_review_note) {
      fields.push({
        key: "revisionNote",
        label: t("amendment.revisionNote"),
        value: amendment.application_review_note,
        icon: Edit2,
      });
    }

    fields.push({
      key: "status",
      label: t("amendment.status"),
      value: "",
      icon: DocumentText1,
      isStatus: true,
      statusComponent: getStatusBadge(amendment, status),
    });

    return (
      <>
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
          <h4 className="text-display-2xs content-base-primary">{t("amendment.title")}</h4>
        </div>

        <div className="flex-1 p-1">
          <div className="flex flex-col py-4">
            <div className="flex flex-col gap-2 mb-4 border-b surface-base-stroke pb-3">
              <p className="text-body-bold-lg content-base-primary">
                {t("amendment.prefix")} №{amendment.amendment_number}
              </p>
            </div>

            <div className="flex flex-col">
              {fields.filter(filterField).map(function (field, index, array) {
                return renderField(field, index, array);
              })}

              {shouldShowApplicationPdf && (
                <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke mt-2">
                  <span className="text-body-regular-md content-base-secondary">
                    {t("amendment.applicationFileLabel")}
                  </span>
                  <FileViewer
                    existingFileUrl={
                      (amendment.application_document.signed_url ||
                        amendment.application_document.original_url) as string
                    }
                    secondaryFileUrl={
                      amendment.application_document.signed_url && amendment.application_document.original_url
                        ? amendment.application_document.original_url
                        : undefined
                    }
                  />
                </div>
              )}

              {(amendment.order_document.signed_url || amendment.order_document.original_url) && (
                <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke mt-2">
                  <span className="text-body-regular-md content-base-secondary">{t("amendment.orderFileLabel")}</span>
                  <FileViewer
                    existingFileUrl={
                      (amendment.order_document.signed_url || amendment.order_document.original_url) as string
                    }
                    secondaryFileUrl={
                      amendment.order_document.signed_url && amendment.order_document.original_url
                        ? amendment.order_document.original_url
                        : undefined
                    }
                  />
                </div>
              )}

              {(amendment.agreement_document.signed_url || amendment.agreement_document.original_url) && (
                <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke mt-2">
                  <span className="text-body-regular-md content-base-secondary">
                    {t("amendment.agreementFileLabel")}
                  </span>
                  <FileViewer
                    existingFileUrl={
                      (amendment.agreement_document.signed_url || amendment.agreement_document.original_url) as string
                    }
                    secondaryFileUrl={
                      amendment.agreement_document.signed_url && amendment.agreement_document.original_url
                        ? amendment.agreement_document.original_url
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>

      </>
    );
  }

  function renderFooter() {
    if (isLoading || !amendmentData) return undefined;

    const amendment = amendmentData;
    const status = amendment.status as AmendmentStatus;

    const { actions: allActions } = getAvailableActions(
      amendment,
      {
        isApproving,
        isRejecting,
        isRequestingRevision,
        isSigning: applicationSigning.isLoading,
        isDownloadingOrder,
        isSubmittingAgreement,
      },
      t,
    );

    const actions = filterActionsByPermissions(allActions, hasPermission, hasRole);

    const hasSigningRevoke =
      amendment.signing_document &&
      (amendment.signing_document.status === 0 || amendment.signing_document.status === 1) &&
      user?.iin === amendment.signing_document.org_signer_iin;

    const hasAgreementRevoke =
      amendment.agreement_signing_document &&
      (amendment.agreement_signing_document.status === 0 || amendment.agreement_signing_document.status === 1) &&
      user?.iin === amendment.agreement_signing_document.org_signer_iin;

    let buttonCount = actions.length;
    if (hasSigningRevoke) buttonCount++;
    if (hasAgreementRevoke) buttonCount++;

    if (buttonCount === 0) return undefined;

    const buttonSize: "lg" | "huge" = buttonCount >= 4 ? "lg" : "huge";

    function renderActionButton(action: (typeof actions)[number]) {
      const isDisabled =
        (action.id === "approve" && (isApproving || applicationSigning.isLoading)) ||
        (action.id === "reject_draft" && isRejecting) ||
        (action.id === "request_revision" && isRequestingRevision) ||
        (action.id === "revoke" && isRevoking) ||
        (action.id === "sign_order" && orderSigning.isLoading) ||
        (action.id === "download_order_docx" && isDownloadingOrder) ||
        (action.id === "submit_agreement" && isSubmittingAgreement);

      return (
        <Button
          key={action.id}
          variant={action.variant}
          size={buttonSize}
          className="w-full"
          disabled={isDisabled}
          onClick={() => handleAction(action.id)}>
          {action.icon}
          {action.label}
        </Button>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {status === "agreement_signed" && amendment.effective_date && (
          <Toast
            color="positive"
            text={t("amendment.deferredEffectiveDate", {
              date: formatDateForDisplay(amendment.effective_date, false),
            })}
            isFullWidth
            closable={false}
            autoClose={false}
          />
        )}
        {actions.some((a) => a.id === "submit_agreement") && (
          <Toast
            color="positive"
            text={t("amendment.actions.submitAgreementInfo")}
            isFullWidth
            closable={false}
            autoClose={false}
          />
        )}
        <DetailModalFooterActions
          actions={actions}
          renderButton={function renderButton(action) {
            return renderActionButton(action as (typeof actions)[number]);
          }}
        />
        {hasSigningRevoke && (
          <RevokeSigningButton
            size={buttonSize}
            documentId={amendment.signing_document!.document_id}
            onSuccess={() => refetchAmendment()}
          />
        )}
        {hasAgreementRevoke && (
          <RevokeSigningButton
            size={buttonSize}
            documentId={amendment.agreement_signing_document!.document_id}
            onSuccess={() => refetchAmendment()}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {showConfirmSubmitAgreement && (
        <PromptForm
          variant="warning"
          onClose={() => setShowConfirmSubmitAgreement(false)}
          onConfirm={handleConfirmSubmitAgreement}
          title={t("prompt_form.title")}
          text={t("prompt_form.description")}
          isLoading={isSubmittingAgreement}
          namespace="ContractsPage"
        />
      )}

      {showConfirmRevoke && (
        <PromptForm
          variant="error"
          onClose={() => setShowConfirmRevoke(false)}
          onConfirm={handleRevokeConfirm}
          title={t("amendment.actions.revokeConfirmTitle")}
          text={t("amendment.actions.revokeConfirmText")}
          isLoading={isRevoking}
          namespace="ContractsPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
          namespace="ContractsPage"
        />
      )}

      {applicationSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${applicationSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {orderSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${orderSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {agreementSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${agreementSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {showRejectNoteModal && (
        <ReviewNoteModal
          action="reject"
          onClose={() => setShowRejectNoteModal(false)}
          onSubmit={handleRejectWithNote}
          isSubmitting={isRejecting}
          namespace="ContractsPage"
          translationPrefix="amendment.reject"
        />
      )}

      {showRevisionNoteModal && (
        <ReviewNoteModal
          action="revision"
          onClose={() => setShowRevisionNoteModal(false)}
          onSubmit={handleRequestRevisionWithNote}
          isSubmitting={isRequestingRevision}
          namespace="ContractsPage"
          translationPrefix="amendment.requestRevision"
        />
      )}

      {previewingOrderAmendmentId !== null && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("amendment.messages.loader.downloadingOrder")}
          additionalText={t("amendment.messages.loader.pleaseWait")}
          namespace="ContractsPage"
        />
      )}

      <ModalForm icon={DocumentText1} onClose={onClose} resize={false}footer={renderFooter()}>
        <div className="flex flex-col gap-6 h-full min-h-0">
          {isLoading ? (
            <>
              <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
                <Skeleton height={28} width={240} />
              </div>
              <div className="flex-1">
                <div className="flex flex-col py-4">
                  <div className="flex flex-col gap-2 mb-4 border-b surface-base-stroke pb-3">
                    <Skeleton height={24} width={200} />
                    <Skeleton height={32} width={150} />
                  </div>
                  <div className="flex flex-col gap-3">
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <div key={idx} className="py-3 border-b surface-base-stroke">
                        <Skeleton height={20} width="100%" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Skeleton height={44} width="100%" />
            </>
          ) : isError || !amendmentData ? (
            <>
              <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
                <h4 className="text-display-2xs content-base-primary">{t("amendment.title")}</h4>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <p className="text-body-regular-md content-action-negative">{t("amendment.loadError")}</p>
              </div>
            </>
          ) : (
            renderAmendmentContent(amendmentData)
          )}
        </div>
      </ModalForm>
    </>
  );
}
